import React, { Fragment, useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
} from "react-table";
import {
  getPrefectJobs,
  getPrefectFlowRunCounts,
  getPrefectDeployments,
} from "../../../services/PrefectService";
import ClusterPoolOne from "./ClusterPoolOne";
import ClusterPoolTwo from "./ClusterPoolTwo";
import PageTitle from "../../layouts/PageTitle";
import {
  Row,
  Col,
  Card,
  ButtonGroup,
  Button,
  Table,
  Alert,
} from "react-bootstrap";
import { useOktaAuth } from "@okta/okta-react";
import moment from "moment";
import { useMsal } from "@azure/msal-react";

// A simple column filter component for react-table
const ColumnFilter = ({ column }) => {
  const { filterValue, setFilter } = column;
  return (
    <div>
      <input
        className="form-control input-search"
        value={filterValue || ""}
        onChange={(e) => setFilter(e.target.value)}
      />
    </div>
  );
};

const PrefectDashboard = () => {
  // Data states
  const [prefectJobs, setPrefectJobs] = useState([]);
  const [prefectFlowRunCounts, setPrefectFlowRunCounts] = useState([]);
  const [prefectDeployments, setPrefectDeployments] = useState([]);
  const [lastRefreshTime, setLastRefreshTime] = useState(
    moment().format("YYYY-MM-DD HH:mm:ss")
  );
  const [loadingElement, setLoadingElement] = useState(true);
  const [showEdmAlert, setShowEdmAlert] = useState(false);
  const [env, setEnv] = useState("prod");

  // Active view state; default is Cluster Pool 1
  const [activeView, setActiveView] = useState("clusters");

  // Function to refresh all data
  const handleRescan = async () => {
    setLoadingElement(true);
    try {
      // Fetch the deployments data (e.g. for Cluster Pool 1 and 2)
      const deploymentsData = await getPrefectDeployments(env);
      setPrefectDeployments(deploymentsData);

      // Fetch additional data: jobs and flow run counts
      const jobsData = await getPrefectJobs();
      const sortedFlowRuns = jobsData.sort(
        (a, b) => new Date(b.run_date) - new Date(a.run_date)
      );
      setPrefectJobs(sortedFlowRuns);

      const flowRunCountsData = await getPrefectFlowRunCounts();
      setPrefectFlowRunCounts(flowRunCountsData);

      setLastRefreshTime(moment().format("YYYY-MM-DD HH:mm:ss"));
    } catch (error) {
      console.error("Error during rescan:", error);
    } finally {
      setLoadingElement(false);
    }
  };

  // -------------------------
  // 1. Load default view (Cluster Pool 1) data first
  // -------------------------
  useEffect(() => {
    async function loadClusterPool1Data() {
      try {
        // Only fetch the deployments data needed for Cluster Pool 1 initially
        console.log("Loading Cluster Pool 1 data for environment:", env);
        const deploymentsData = await getPrefectDeployments(env);
        setPrefectDeployments(deploymentsData);
        setLoadingElement(false);
        setLastRefreshTime(moment().format("YYYY-MM-DD HH:mm:ss"));
      } catch (error) {
        console.error("Error loading Cluster Pool 1 data:", error);
        setLoadingElement(false);
      }
    }
    loadClusterPool1Data();
  }, [env]);

  // -------------------------
  // 2. Load additional data in the background
  // -------------------------
  useEffect(() => {
    async function loadOtherData() {
      try {
        const jobsData = await getPrefectJobs();
        const sortedFlowRuns = jobsData.sort(
          (a, b) => new Date(b.run_date) - new Date(a.run_date)
        );
        setPrefectJobs(sortedFlowRuns);

        const flowRunCountsData = await getPrefectFlowRunCounts();
        setPrefectFlowRunCounts(flowRunCountsData);
      } catch (error) {
        console.error("Error loading additional data:", error);
      }
    }
    loadOtherData();
  }, []);

  // Table column definitions for Prefect Flow Runs
  const columns = useMemo(
    () => [
      {
        Header: "Status",
        Filter: ColumnFilter,
        accessor: "state",
        Cell: ({ cell: { value } }) => {
          let statusClass = "";
          switch (value.toUpperCase()) {
            case "COMPLETED":
              statusClass = "text-primary";
              break;
            case "FAILED":
              statusClass = "text-danger";
              break;
            case "PENDING":
              statusClass = "text-warning";
              break;
            case "CANCELLED":
              statusClass = "text-danger";
              break;
            case "CRASHED":
              statusClass = "text-danger";
              break;
            case "PAUSED":
              statusClass = "text-info";
              break;
            case "CANCELLING":
              statusClass = "text-warning";
              break;
            default:
              statusClass = "text-primary";
          }
          return <span className={statusClass}>{value.toUpperCase()}</span>;
        },
      },
      {
        Header: "Run Again",
        Cell: ({ row }) => {
          const { run_failure_count } = row.original;
          return run_failure_count > 0 ? (
            <Button
              className="me-2"
              variant="outline-secondary"
              disabled
              style={{ color: "#24365c" }}
            >
              Can't rerun
            </Button>
          ) : (
            <Button className="me-2" variant="outline-secondary">
              Re-run
            </Button>
          );
        },
      },
      {
        Header: "Flow Name",
        accessor: "flow_name",
        Filter: ColumnFilter,
      },
      {
        Header: "Flow Run Name",
        accessor: "flow_run_name",
        Filter: ColumnFilter,
      },
      {
        Header: "Run Date",
        accessor: "run_date",
        Filter: ColumnFilter,
      },
      {
        Header: "Run Duration",
        accessor: "run_duration_formatted",
        Filter: ColumnFilter,
      },
      {
        Header: "Url",
        accessor: "url",
        Filter: ColumnFilter,
        Cell: ({ cell: { value } }) => (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ),
      },
      {
        Header: "Deployment ID",
        accessor: "deployment_id",
        Filter: ColumnFilter,
      },
      {
        Header: "Work Pool",
        accessor: "work_pool",
        Filter: ColumnFilter,
      },
      {
        Header: "Workspace",
        accessor: "workspace",
        Filter: ColumnFilter,
      },
    ],
    []
  );

  // Column definitions for Prefect Flow Run Counts table
  const flowRunCountsColumns = useMemo(
    () => [
      {
        Header: "Status",
        Filter: ColumnFilter,
        accessor: "state",
        Cell: ({ cell: { value } }) => {
          let statusClass = "";
          switch (value.toUpperCase()) {
            case "COMPLETED":
              statusClass = "text-primary";
              break;
            case "FAILED":
              statusClass = "text-danger";
              break;
            case "PENDING":
              statusClass = "text-warning";
              break;
            case "CANCELLED":
              statusClass = "text-danger";
              break;
            case "CRASHED":
              statusClass = "text-danger";
              break;
            case "PAUSED":
              statusClass = "text-info";
              break;
            case "CANCELLING":
              statusClass = "text-warning";
              break;
            default:
              statusClass = "text-primary";
          }
          return <span className={statusClass}>{value}</span>;
        },
      },
      {
        Header: "Run Again",
        Cell: ({ row }) => {
          const { run_failure_count } = row.original;
          return run_failure_count > 0 ? (
            <Button
              className="me-2"
              variant="outline-secondary"
              disabled
              style={{ color: "#24365c" }}
            >
              Can't rerun
            </Button>
          ) : (
            <Button className="me-2" variant="outline-secondary">
              Re-run
            </Button>
          );
        },
      },
      {
        Header: "Flow Runs",
        accessor: "flow_runs",
        Filter: ColumnFilter,
      },
      {
        Header: "Work Pool",
        accessor: "work_pool",
        Filter: ColumnFilter,
      },
      {
        Header: "Workspace",
        accessor: "workspace",
        Filter: ColumnFilter,
      },
      {
        Header: "Run Date",
        accessor: "run_date",
        Filter: ColumnFilter,
      },
    ],
    []
  );

  // Memoized data for the tables
  const data = useMemo(() => prefectJobs || [], [prefectJobs]);
  const flowRunCountsData = useMemo(
    () => prefectFlowRunCounts || [],
    [prefectFlowRunCounts]
  );

  // Set up React Table instances
  const tableInstance = useTable(
    { columns, data },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canNextPage,
    canPreviousPage,
    nextPage,
    previousPage,
    gotoPage,
    pageCount,
    pageOptions,
    state,
  } = tableInstance;

  const deploymentTableInstance = useTable(
    { columns: flowRunCountsColumns, data: flowRunCountsData },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );
  const {
    getTableProps: getDeploymentTableProps,
    getTableBodyProps: getDeploymentTableBodyProps,
    headerGroups: deploymentHeaderGroups,
    prepareRow: prepareDeploymentRow,
    page: deploymentPage,
    canNextPage: deploymentCanNextPage,
    canPreviousPage: deploymentCanPreviousPage,
    nextPage: deploymentNextPage,
    previousPage: deploymentPreviousPage,
    gotoPage: deploymentGotoPage,
    pageCount: deploymentPageCount,
  } = deploymentTableInstance;
  const { pageIndex } = state;

  // Authentication info
  const { accounts } = useMsal();
  const username = useMemo(() => accounts[0]?.name ?? "Unknown", [accounts]);

  // EDM alert handler
  const handleEDMTeamConnect = () => {
    setShowEdmAlert(true);
  };

  // Render view based on the activeView state; each card includes a "Rescan Data" button
  const renderContent = () => {
    switch (activeView) {
      case "clusters":
        return (
          <Card>
            <Card.Header>
              <Card.Title>Prefect Deployments (Clusters)</Card.Title>
              {lastRefreshTime && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.9rem",
                    color: "#3d4465",
                  }}
                >
                  Last refreshed: {lastRefreshTime}
                </div>
              )}
            </Card.Header>
            <Card.Body>
              <ClusterPoolOne dynamicData={prefectDeployments} />
              <ClusterPoolTwo dynamicData={prefectDeployments} />
            </Card.Body>
            <div className="row mb-3">
              <div className="col text-center">
                <Button variant="primary" onClick={handleRescan}>
                  Rescan Data
                </Button>
              </div>
            </div>
          </Card>
        );

        return (
          <Card>
            <Card.Header>
              <Card.Title>Prefect Deployments (Cluster Pool 2)</Card.Title>
              {lastRefreshTime && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.9rem",
                    color: "#3d4465",
                  }}
                >
                  Last refreshed: {lastRefreshTime}
                </div>
              )}
            </Card.Header>
            <Card.Body>
              <ClusterPoolTwo dynamicData={prefectDeployments} />
              <ClusterPoolOne dynamicData={prefectDeployments} />
            </Card.Body>
            <div className="row mb-3">
              <div className="col text-center">
                <Button variant="primary" onClick={handleRescan}>
                  Rescan Data
                </Button>
              </div>
            </div>
          </Card>
        );
      case "prefectFlowRuns":
        return (
          <Card>
            <Card.Header>
              <Card.Title>Prefect Flow Runs</Card.Title>
              {lastRefreshTime && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.9rem",
                    color: "#3d4465",
                  }}
                >
                  Last refreshed: {lastRefreshTime}
                </div>
              )}
            </Card.Header>
            <Card.Body>
              <Table {...getTableProps()} responsive>
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                          style={{ cursor: "pointer", verticalAlign: "top" }}
                        >
                          {column.render("Header")}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " 🔽"
                                : " 🔼"
                              : ""}
                          </span>
                          {column.canFilter ? (
                            <div>{column.render("Filter")}</div>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
            <div className="row mb-3">
              <div className="col text-center">
                <Button variant="primary" onClick={handleRescan}>
                  Rescan Data
                </Button>
              </div>
            </div>
          </Card>
        );
      case "prefectFlowRunCounts":
        return (
          <Card>
            <Card.Header>
              <Card.Title>Prefect Flow Run Counts</Card.Title>
            </Card.Header>
            <Card.Body>
              <Table {...getDeploymentTableProps()} responsive>
                <thead>
                  {deploymentHeaderGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(
                            column.getSortByToggleProps()
                          )}
                          style={{ cursor: "pointer", verticalAlign: "top" }}
                        >
                          {column.render("Header")}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? " 🔽"
                                : " 🔼"
                              : ""}
                          </span>
                          {column.canFilter ? (
                            <div>{column.render("Filter")}</div>
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getDeploymentTableBodyProps()}>
                  {deploymentPage.map((row) => {
                    prepareDeploymentRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
            <div className="row mb-3">
              <div className="col text-center">
                <Button variant="primary" onClick={handleRescan}>
                  Rescan Data
                </Button>
              </div>
            </div>
          </Card>
        );
      default:
        return (
          <Card>
            <Card.Header>
              <Card.Title>Prefect Deployments (Clusters)</Card.Title>
              {lastRefreshTime && (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.9rem",
                    color: "#3d4465",
                  }}
                >
                  Last refreshed: {lastRefreshTime}
                </div>
              )}
            </Card.Header>
            <Card.Body>
              <ClusterPoolOne dynamicData={prefectDeployments} />
              <ClusterPoolTwo dynamicData={prefectDeployments} />
            </Card.Body>
            <div className="row mb-3">
              <div className="col text-center">
                <Button variant="primary" onClick={handleRescan}>
                  Rescan Data
                </Button>
              </div>
            </div>
          </Card>
        );
    }
  };

  return (
    <Fragment>
      <div className="row pt-5">
        <h1 className="title text-center">Prefect Trust Portal</h1>
      </div>
      <div className="row justify-content-center">
        <div className="col-md-6">
          <ButtonGroup className="env-toggle">
            <Button
              variant={env === "prod" ? "primary" : "outline-primary"}
              onClick={() => {
                setEnv("prod");
                handleRescan("prod");
              }}
              className={env === "prod" ? "active" : ""}
            >
              PROD
            </Button>
            <Button
              variant={env === "dev" ? "primary" : "outline-primary"}
              onClick={() => {
                setEnv("dev");

                handleRescan("dev");
              }}
              className={env === "dev" ? "active" : ""}
              disabled={true}
            >
              DEV
            </Button>
          </ButtonGroup>
        </div>
      </div>
      <style jsx>{`
        .env-toggle {
          display: flex;
          justify-content: center;
          margin: 20px 0;
        }
        .env-toggle .btn {
          min-width: 120px;
          transition: all 0.3s ease;
          border-radius: 0;
        }
        .env-toggle .btn:first-child {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }
        .env-toggle .btn:last-child {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
        .env-toggle .btn.active {
          font-weight: bold;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
      `}</style>

      {/* Button group to switch between views */}
      <Row className="mb-3 pt-5">
        <Col className="text-center">
          <ButtonGroup>
            <Button
              variant={
                activeView === "clusters" ? "primary" : "outline-primary"
              }
              onClick={() => setActiveView("clusters")}
              style={{ minWidth: "200px" }}
            >
              Clusters
            </Button>

            <Button
              variant={
                activeView === "prefectFlowRuns" ? "primary" : "outline-primary"
              }
              onClick={() => setActiveView("prefectFlowRuns")}
              style={{ minWidth: "200px" }}
            >
              Prefect Flow Runs
            </Button>
            <Button
              variant={
                activeView === "prefectFlowRunCounts"
                  ? "primary"
                  : "outline-primary"
              }
              onClick={() => setActiveView("prefectFlowRunCounts")}
              style={{ minWidth: "200px" }}
            >
              Prefect Flow Run Counts
            </Button>
          </ButtonGroup>
        </Col>
      </Row>
      <Col className="mt-5" lg={12}>
        {renderContent()}
      </Col>
      <Row className="justify-content-center">
        <Col
          xs={12}
          md={3}
          style={{
            paddingLeft: "0",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            style={{ width: "100%", maxWidth: "200px" }}
            variant="light"
            onClick={handleEDMTeamConnect}
          >
            Connect with EDM Team
          </Button>
        </Col>
      </Row>
      {showEdmAlert && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "55%",
            transform: "translate(-50%, -50%)",
            zIndex: 1050,
            width: "80%",
            maxWidth: "600px",
          }}
        >
          <Alert
            variant="secondary"
            dismissible
            onClose={() => setShowEdmAlert(false)}
            className="solid"
          >
            <strong>Done! </strong> EDM team has been notified and will reach
            out to you via email shortly.
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowEdmAlert(false)}
            >
              <span>
                <i className="mdi mdi-close" />
              </span>
            </button>
          </Alert>
        </div>
      )}
      {loadingElement && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1050,
          }}
        >
          <div
            style={{
              width: "1.5rem",
              height: "1.5rem",
              border: "0.2rem solid rgba(0, 0, 255, 0.3)",
              borderTop: "0.2rem solid blue",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
        </div>
      )}
    </Fragment>
  );
};

export default PrefectDashboard;
